import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import classNames from 'classnames'

import { useQuery } from '@/utils/hooks/useQuery'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Pill } from '@/components/pills/Pill'
import { setMassPlaybookUpdateSelection } from '@/reducers/playbooks/playbooks.redux'
import { checklistValidationSchema, checklistInitialValues } from './checklist.schema'
import { ChecklistForm } from './ChecklistForm'
import { ChecklistNestedPreview } from './ChecklistNestedPreview'
import { PlaybookPreview } from '../../components/PlaybookPreview'
import { PlaybookEditor } from '../../components/PlaybookEditor'
import { accessorSectionMap, massPlaybookUpdateProgressStatus } from '../../playbook.helpers'
import { LoadingPlaybooks, SelectionActions } from '../shared/MassPlaybookUpdateComponents'
import { getColumns } from './ChecklistBulkEditColumns'

export const ChecklistEditor = () => {
  const dispatch = useDispatch()
  const { entryId, section } = useParams()
  const { playbook, listIDsInDB = {} } = useSelector((state) => state.playbook)
  const { loading, massPlaybookUpdate } = useSelector((state) => state.playbooks)
  const { selectedHierarchyOrganizations } = useSelector((state) => state.analytics)
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const { organizationid, hierarchy_manager: isHierarchyManager } = useSelector(
    (state) => state.currentUser
  )

  const query = useQuery()
  const { isEnabled, progress, matches, fetchFailed, selection } = massPlaybookUpdate || {}
  const accessor = accessorSectionMap[section]
  const isCreate = entryId === 'create'
  const categoryId = query.get('category_id')
  const entry = playbook.body[accessor].entries[entryId]

  const getInitialValues = () => {
    if (!isCreate) {
      return entry
    }

    return checklistInitialValues
  }

  const initialValues = getInitialValues()
  const isExistingItem = listIDsInDB.checklist.includes(entryId)
  const showMassPlaybookUpdateContent = !loading && isEnabled
  const isBaltoAdmin = organizationid === 1
  const organizationIds = flattenedUserOrgHierarchy.map((org) => org.organization_id)
  const canSeeOrgHierarchy = !isBaltoAdmin && isHierarchyManager && organizationIds.length > 1

  const handleSelectSingle = (compoundId) => {
    if (progress !== massPlaybookUpdateProgressStatus.INITIAL) return
    if (!selection.includes(compoundId)) {
      dispatch(setMassPlaybookUpdateSelection([...selection, compoundId]))
    } else {
      dispatch(
        setMassPlaybookUpdateSelection(selection.filter((selectedId) => selectedId !== compoundId))
      )
    }
  }

  const matchesFiltered = matches.filter(
    (m) =>
      isBaltoAdmin ||
      isEmpty(selectedHierarchyOrganizations) ||
      selectedHierarchyOrganizations.includes(m.organizationId)
  )
  const allSelected = matchesFiltered.length === selection.length

  const handleSelectAll = () => {
    if (progress !== massPlaybookUpdateProgressStatus.INITIAL) return
    if (allSelected) {
      dispatch(setMassPlaybookUpdateSelection([]))
    } else {
      dispatch(setMassPlaybookUpdateSelection(matchesFiltered.map((row) => row.compoundId)))
    }
  }

  useEffect(() => {
    // Check if some organization is removed (from organization picker) and then remove matches that belong to that org in selection
    const toRemove = []
    selection.forEach((compoundId) => {
      const match = matches.find((m) => m.compoundId === compoundId)
      if (match && !selectedHierarchyOrganizations.includes(match.organizationId))
        toRemove.push(match.compoundId)
    })
    if (!isEmpty(toRemove)) {
      const newSelection = selection.filter((compoundId) => !toRemove.includes(compoundId))
      dispatch(setMassPlaybookUpdateSelection(newSelection))
    }
  }, [selectedHierarchyOrganizations])

  return (
    <PlaybookEditor entry={entry} categoryId={isCreate ? categoryId : undefined}>
      {({ handleUpdate, handleCancel, readOnly }) => (
        <Formik
          initialValues={initialValues}
          validationSchema={checklistValidationSchema}
          onSubmit={handleUpdate}
          enableReinitialize
        >
          {({ values, initialValues, handleSubmit, errors, ...formikProps }) => (
            <div className="checklist-form-container">
              <ChecklistForm
                isCreate={isCreate && progress === massPlaybookUpdateProgressStatus.INITIAL}
                readOnly={readOnly}
                errors={errors}
                handleSubmit={handleSubmit}
                handleCancel={handleCancel}
                entryId={entryId}
                values={values}
                initialValues={initialValues}
                {...formikProps}
              />
              <div className="playbook-checklist-section__scrollable">
                <PlaybookPreview values={values} section={section} title="Checklist">
                  <ChecklistNestedPreview entryId={entryId} values={values} isCreate={isCreate} />
                </PlaybookPreview>
                <LoadingPlaybooks
                  loading={loading}
                  isEnabled={isEnabled}
                  isUpdate={isExistingItem}
                />
                {showMassPlaybookUpdateContent && (
                  <>
                    <div className="flex-center mtl">
                      <Pill small emphasized>
                        Other Playbooks
                      </Pill>
                    </div>
                    <div className="padding-large">
                      <div
                        className={classNames('post-call-table-wrap', {
                          'post-call-table-wrap--loading': isEmpty(matches),
                        })}
                      >
                        {!isEmpty(matchesFiltered) && !fetchFailed && (
                          <p style={{ fontSize: '14px', fontWeight: 600 }}>
                            {`Select at least one playbook to ${isExistingItem ? 'edit' : 'add'} Checklist in bulk.`}
                          </p>
                        )}
                        <AdvancedTable
                          columns={getColumns(isExistingItem, progress, canSeeOrgHierarchy)}
                          rows={matchesFiltered}
                          selectedRows={selection}
                          index="compoundId"
                          bulkSelectable={progress === massPlaybookUpdateProgressStatus.INITIAL}
                          bulkSelectAction
                          customBulkSelectBanner={
                            <SelectionActions
                              selection={selection}
                              allSelected={allSelected}
                              matches={matchesFiltered}
                              handleSelectAll={handleSelectAll}
                              isOnTop
                            />
                          }
                          handleBulkSelectSingle={handleSelectSingle}
                          handleDeselectAll={handleSelectAll}
                          handleSelectAll={handleSelectAll}
                          disabledSelection={progress !== massPlaybookUpdateProgressStatus.INITIAL}
                          itemName="checklist"
                          noDataHeader={
                            fetchFailed
                              ? 'Failed to get phrase suggestions for bulk editing'
                              : undefined
                          }
                          noDataText={
                            fetchFailed
                              ? 'Please retry by reloading or reopening the page'
                              : 'No similar content found across your Playbooks'
                          }
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </Formik>
      )}
    </PlaybookEditor>
  )
}
